import { lazy } from "react"

// ** Third Party Components
import { useRoutes } from "react-router-dom"

// ** Routes
import {
  ACTIVITIES_PAGE_ROUTE,
  ANALYTICS_PAGE_ROUTE,
  DASHBOARD_PAGE_ROUTE,
  FILES_FOLDER_PAGE_ROUTE,
  FILES_PAGE_ROUTE,
  FINANCIAL_BOQ_MAIN_ROUTE,
  FINANCIAL_BOQ_ROUTE,
  FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
  FINANCIAL_PAGE_ROUTE,
  FINANCIAL_PRICE_LIST_PAGE_ROUTE,
  HOME_PAGE_ROUTE,
  INVITATIONS_ACCEPT_PAGE_ROUTE,
  INVITATIONS_PAGE_ROUTE,
  LOGIN_COMPLETE_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
  NEW_DASHBOARD_PAGE_ROUTE,
  PINNED_PROJECT_PAGE_ROUTE,
  PROFILE_PAGE_ROUTE,
  PROJECT_GROUP_PAGE_ROUTE,
  PROJECT_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
  RESET_PASSWORD_LINK_PAGE_ROUTE,
  RESET_PASSWORD_PAGE_ROUTE,
  SETTING_PAGE_ROUTE,
  SETTINGS_PAGE_ROUTE,
  SIGNUP_PAGE_ROUTE,
  USER_PAGE_ROUTE,
  USERS_PAGE_ROUTE,
  FINANCE_PAGE_ROUTE,
  MY_TASKS_PAGE_ROUTE,
} from "@pma/utils/page-routes"

import CompletePage from "@pma/app/auth/complete"
import InvitationsPage from "@pma/app/auth/invitation"
import InvitationsAcceptPage from "@pma/app/auth/invitation-accept"
import LoginPage from "@pma/app/auth/login"
import ResetPasswordPage from "@pma/app/auth/password"
import ResetPasswordLinkPage from "@pma/app/auth/reset-password"
import SignupPage from "@pma/app/auth/signup"
import ActivitiesPage from "@pma/app/dashboard/activities"
import AnalyticsPage from "@pma/app/dashboard/analytics"
import FilesPage from "@pma/app/dashboard/files"
import BoQPage from "@pma/app/dashboard/financial/boq/boq"
import EstimateBoQDetailPage from "@pma/app/dashboard/financial/boq/estimate-boq-detail"
import MainBoQPage from "@pma/app/dashboard/financial/boq/main/main-boq"
import MainBoQDetailPage from "@pma/app/dashboard/financial/boq/main/main-boq-detail"
import ContractorsPage from "@pma/app/dashboard/financial/contractors/contractors"
import ContractorsDetailPage from "@pma/app/dashboard/financial/contractors/contractors-details-page"
import FinancialPage from "@pma/app/dashboard/financial/financial"
import PriceListDetailPage from "@pma/app/dashboard/financial/price-lists/price-list-detail"
import PriceListsPage from "@pma/app/dashboard/financial/price-lists/price-lists"
import ProjectDetailPage from "@pma/app/dashboard/projects/project-detail"
import ProjectsPage from "@pma/app/dashboard/projects/projects"
import ProfilePage from "@pma/app/dashboard/settings/profile"
import SettingsPage from "@pma/app/dashboard/settings/settings"
import UsersPage from "@pma/app/dashboard/users/users"
import UsersDetailPage from "@pma/app/dashboard/users/users-detail"
import NotFound from "@pma/app/not-found"
import LandingPage from "@pma/app/welcome/landing"
import AdminGuard from "@pma/guard/AdminGuard"
import AuthGuard from "@pma/guard/AuthGuard"
import FinancialGuard from "@pma/guard/FinancialGuard"
import MainLayout from "@pma/layouts/mainLayout"
import DashboardHomePage from "@pma/app/dashboard/home"

const Files = lazy(() => import("../pages/Files"))
const Users = lazy(() => import("../pages/User"))
const Project = lazy(() => import("../pages/Project"))
const Dashboard = lazy(() => import("../pages/Dashboard"))
const ProjectTask = lazy(() => import("../pages/Project/Task"))
const ContractorCompany = lazy(() => import("../components/Contractor-Company"))
const UserProfileInformation = lazy(() => import("../pages/User/ProfileInformation"))
const Settings = lazy(() => import("../pages/Settings"))
const Finance = lazy(() => import("../pages/Finance"))
const MyTasks = lazy(() => import("../pages/MyTasks"))

const routes = [
  // Invites & Auth
  { path: "/", element: <LandingPage /> },
  { path: HOME_PAGE_ROUTE, element: <LandingPage /> },
  { path: LOGIN_PAGE_ROUTE, element: <LoginPage /> },
  { path: SIGNUP_PAGE_ROUTE, element: <SignupPage /> },
  { path: LOGIN_COMPLETE_PAGE_ROUTE, element: <CompletePage /> },
  { path: RESET_PASSWORD_LINK_PAGE_ROUTE, element: <ResetPasswordLinkPage /> },
  { path: RESET_PASSWORD_PAGE_ROUTE, element: <ResetPasswordPage /> },
  { path: INVITATIONS_PAGE_ROUTE, element: <InvitationsPage /> },
  { path: INVITATIONS_ACCEPT_PAGE_ROUTE, element: <InvitationsAcceptPage /> },

  // PMA
  { path: DASHBOARD_PAGE_ROUTE, element: <DashboardHomePage /> },
  { path: USERS_PAGE_ROUTE, element: <UsersPage /> },
  { path: `${USERS_PAGE_ROUTE}/:userId`, element: <UsersDetailPage /> },
  { path: PROJECTS_PAGE_ROUTE, element: <ProjectsPage /> },
  { path: `${PROJECTS_PAGE_ROUTE}/:projectId`, element: <ProjectDetailPage /> },
  { path: ACTIVITIES_PAGE_ROUTE, element: <ActivitiesPage /> },
  { path: ANALYTICS_PAGE_ROUTE, element: <AnalyticsPage /> },
  // { path: GIS_PAGE_ROUTE, element: <GisPage /> }, // Commented as in original

  { path: FILES_PAGE_ROUTE, element: <FilesPage /> },

  // Settings
  { path: SETTINGS_PAGE_ROUTE, element: <SettingsPage /> },
  { path: PROFILE_PAGE_ROUTE, element: <ProfilePage /> },

  // AuthGuard Protected Routes
  {
    element: <AuthGuard />,
    children: [
      {
        path: "/",
        element: <MainLayout />,
        children: [
          { path: NEW_DASHBOARD_PAGE_ROUTE, element: <Dashboard /> },
          { path: PROJECT_PAGE_ROUTE, element: <Project /> },
          { path: PINNED_PROJECT_PAGE_ROUTE, element: <Project /> },
          { path: `${PROJECT_GROUP_PAGE_ROUTE}/:groupId`, element: <Project /> },
          { path: `${PINNED_PROJECT_PAGE_ROUTE}/:projectId`, element: <ProjectTask /> },
          { path: `${PROJECT_PAGE_ROUTE}/:projectId`, element: <ProjectTask /> },
          { path: FILES_FOLDER_PAGE_ROUTE, element: <Files /> },
          { path: `${FILES_FOLDER_PAGE_ROUTE}/:folderId`, element: <Files /> },
          { path: `${USER_PAGE_ROUTE}/:userId`, element: <UserProfileInformation /> },
          { path: MY_TASKS_PAGE_ROUTE, element: <MyTasks /> },
          { path: USER_PAGE_ROUTE, element: <Users /> },
          {
            element: <AdminGuard />,
            children: [{ path: SETTING_PAGE_ROUTE, element: <Settings /> }],
          },
          {
            element: <FinancialGuard />,
            children: [
              { path: FINANCE_PAGE_ROUTE, element: <Finance /> },
              // Financial
              { path: FINANCIAL_PAGE_ROUTE, element: <FinancialPage /> },
              {
                path: FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
                element: <ContractorCompany />,
              },
              {
                path: `${FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE}/:companyId`,
                element: <ContractorsDetailPage />,
              },
              {
                path: FINANCIAL_PRICE_LIST_PAGE_ROUTE,
                element: <PriceListsPage />,
              },
              {
                path: `${FINANCIAL_PRICE_LIST_PAGE_ROUTE}/:priceListId`,
                element: <PriceListDetailPage />,
              },
              {
                path: FINANCIAL_BOQ_ROUTE,
                element: <BoQPage />,
              },
              {
                path: `${FINANCIAL_BOQ_ROUTE}/:boqId`,
                element: <EstimateBoQDetailPage />,
              },
              {
                path: `${FINANCIAL_BOQ_MAIN_ROUTE}/:boqId`,
                element: <MainBoQDetailPage />,
              },
              {
                path: FINANCIAL_BOQ_MAIN_ROUTE,
                element: <MainBoQPage />,
              },
            ],
          },
        ],
      },
    ],
  },

  // Fallback
  { path: "*", element: <NotFound /> },
]

const AppRoutes = () => useRoutes(routes)

export default AppRoutes
